<template>
  <div>
    <label :class="labelClass" class="p-float-label hk-grotest-medium"
      >{{ label }}<span v-if="required" class="required"></span
    ></label>
    <div class="input-wrapper" :class="{ 'has-trailing-icon': trailingIcon }">
      <span v-if="icon" class="icon-container">
        <i :class="icon" class="icons field" />
      </span>
      <InputText
        v-model="editValue"
        class="input-field md:col-6"
        @keydown="keyDown"
        @paste="onPaste"
        :placeholder="placeholder"
        :type="type"
        :inputmode="inputMode"
        :disabled="disabled"
        :pattern="pattern"
        :max="max"
        :min="min"
        :maxLength="nativeMaxLength"
        :style="{
          height: `${height}`,
          width: `${width}`,
          border: `1px solid ${color} !important`,
          color: `${color}`,
        }"
        @input="onInput"
      />
      <!-- Sits inside the field rather than in its own bordered box like `icon`, so it decorates
           the input instead of prefixing it. Decorative only, hence aria-hidden and no pointer
           events — clicking it should focus the field underneath. -->
      <i v-if="trailingIcon" :class="trailingIcon" class="trailing-icon" aria-hidden="true" />
    </div>
    <label v-if="errorMessages" class="error-text required bold-400">{{ errorMessages }}</label>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext';
export default {
  name: 'TextField',
  components: { InputText },
  // `null` keeps the pre-existing props untyped, exactly as the array form did. `numeric` is
  // declared as a real Boolean so a bare `numeric` attribute resolves to true rather than ''.
  props: {
    border: null,
    color: null,
    disabled: null,
    errorMessages: null,
    height: null,
    icon: null,
    label: null,
    labelClass: null,
    max: null,
    maxLength: null,
    min: null,
    // Opt-in: restricts the field to digits and asks mobile keyboards for the numeric keypad.
    // Kept off `type="number"` on purpose — a number input drops the leading zero that AU BSBs
    // rely on (062000) and still lets `e` through.
    numeric: { type: Boolean, default: false },
    pattern: null,
    placeholder: null,
    required: null,
    trailingIcon: null,
    type: null,
    value: null,
    width: null,
  },
  emits: ['onInput', 'change'],
  data() {
    return {
      editValue: '',
    };
  },
  computed: {
    inputMode() {
      return this.numeric ? 'numeric' : undefined;
    },
    // Numeric fields cap themselves in JS instead. The native attribute truncates the raw string
    // before we strip separators, so autofilling `062-000` into a maxlength of 6 would arrive as
    // `062-00` and sanitise down to a wrong 5-digit BSB. The JS cap counts digits, not characters.
    nativeMaxLength() {
      return this.numeric ? undefined : this.maxLength;
    },
    digitLimit() {
      const limit = Number(this.maxLength);
      return Number.isFinite(limit) && limit > 0 ? limit : null;
    },
  },
  methods: {
    onInput({ target }) {
      if (this.numeric) {
        const digitsOnly = this.capDigits(this.stripNonDigits(target.value));
        // Autofill and drag-and-drop bypass keydown, so the value is corrected here too. Writing
        // it back to the DOM keeps what the customer sees identical to what we emit.
        if (digitsOnly !== target.value) {
          target.value = digitsOnly;
          this.editValue = digitsOnly;
        }
        this.$emit('onInput', digitsOnly);
        return;
      }
      this.$emit('onInput', target.value);
    },
    onPaste(event) {
      if (!this.numeric) return;
      // Pasting a formatted BSB (062-000) should land as 062000 rather than being rejected, so
      // the separators are stripped here instead of blocking the paste outright.
      const pasted = (event.clipboardData || window.clipboardData)?.getData('text') ?? '';
      const digitsOnly = this.stripNonDigits(pasted);
      if (digitsOnly === pasted) return;
      event.preventDefault();
      const target = event.target;
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      const next = target.value.slice(0, start) + digitsOnly + target.value.slice(end);
      const capped = this.capDigits(next);
      target.value = capped;
      this.editValue = capped;
      this.$emit('onInput', capped);
    },
    stripNonDigits(value) {
      return (value == null ? '' : String(value)).replace(/\D/g, '');
    },
    capDigits(value) {
      return this.digitLimit ? value.slice(0, this.digitLimit) : value;
    },
    keyDown(value) {
      // The digit cap is enforced on input rather than here so that a keystroke landing mid-value
      // (or replacing a selection) is not blocked just because the field is already full.
      if (this.numeric && this.isNonDigitKeystroke(value)) {
        value.preventDefault();
        return;
      }
      if (this.type == 'number') {
        let invalidChars = ['-', '+', 'e', '.'];
        if (invalidChars.includes(value.key)) {
          value.preventDefault();
        }
      }
    },
    // Only single printable characters are candidates for blocking — named keys (Backspace, Tab,
    // arrows) and modifier combos such as cmd+V must still get through.
    isNonDigitKeystroke(event) {
      if (event.ctrlKey || event.metaKey || event.altKey) return false;
      if (event.key == null || event.key.length !== 1) return false;
      return !/^\d$/.test(event.key);
    },
  },
  watch: {
    editValue() {
      this.$emit('change', this.editValue);
    },
    // `editValue` is seeded once on mount, so without this the input keeps whatever was typed even
    // when the parent stores something different — a parent that sanitises its copy would show the
    // rejected characters. The guard makes it a no-op for parents that echo the value back as-is.
    value(newValue) {
      const next = newValue ?? '';
      if (next !== this.editValue) {
        this.editValue = next;
      }
    },
  },
  mounted() {
    this.editValue = this.value ?? '';
  },
};
</script>

<style lang="scss" scoped>
.input-wrapper {
  display: flex;
  align-items: center;
  position: relative;
}

.trailing-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  // Alpha tuned so the navy lands on the neutral the design uses for the padlock; the token
  // keeps it in step with the white-label palettes.
  color: rgba($secondary-color, 0.34);
  font-size: 1rem;
  pointer-events: none;
}

// Keeps the value clear of the icon it would otherwise run underneath.
.has-trailing-icon .input-field {
  padding-right: 2.25rem;
}
.input-field {
  border-color: unset;
  outline: 0 none;
  outline-offset: 0;
  box-shadow: unset !important;
  border-radius: unset !important;
}

.icons {
  font-weight: bold !important;
  height: 100%;
  font-size: 1.2rem;
  padding: 10px;
  min-width: 40px;
  // border-right: 1px solid #ced4da;
  border: 1px solid #ced4da;
  margin-top: 1rem;
  background: #fff;
}

.p-inputtext {
  border: 1px solid rgba(2, 0, 56, 0.15);
  border-radius: 4px !important;
}
.p-inputtext:enabled:focus {
  border-color: rgba(2, 0, 56, 0.15);
  border: 1px solid rgba(100, 97, 170, 0.3);
  box-shadow: unset;
}
.p-inputtext:enabled:hover {
  border-color: rgba(2, 0, 56, 0.15);
  border: 1px solid rgba(100, 97, 170, 0.3);
  box-shadow: unset;
}
.p-inputtext:enabled:focus {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: 0 0 0 0.1rem rgba(2, 0, 56, 0.3) !important;
  border-color: rgba(2, 0, 56, 0.15);
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.required {
  color: red;
}
.error-text {
  font-size: 14px;
}
</style>
